<template lang="html">
	<form class="omapi-settings-page" @submit.prevent="saveChanges">
		<core-save-button
			v-if="!isLoading"
			size="large"
			color="green"
			class="omapi-settings-page__top-save"
			:disabled="!changed"
			:saving="isSaving"
		/>

		<slot name="tabs"></slot>

		<common-alerts id="om-settings-errors" :alerts="alerts" />

		<settings-not-connected v-if="!connected" />

		<common-card title="Site Settings">
			<core-loading v-if="isLoading" />
			<div v-else>
				<settings-site-cookies
					:global-interaction-cookie="globalInteractionCookie"
					:global-success-cookie="globalSuccessCookie"
					:class="{ disabled: !connected }"
					@toggle="toggleCookieSettings"
					@input="setValue"
				/>

				<settings-site-analytics
					:global-analytics-i-d="globalAnalyticsID"
					:global-analytics-profile="globalAnalyticsProfile"
					:class="{ disabled: !connected }"
					@toggle="toggleAnalyticsSettings"
					@input="setSelectValue"
				/>

				<settings-site-integration
					:default-integration="defaultIntegration"
					:class="{ disabled: !connected }"
					@toggle="toggleDefaultIntegration"
					@input="setSelectValue"
				/>

				<settings-site-affiliate
					:affiliate-link="affiliateLink"
					:affiliate-link-position="affiliateLinkPosition"
					:show-toggle-powered-by="showTogglePoweredBy"
					@input="setValue"
					@select="setSelectValue"
					@click="maybeShowUpgradeModal"
				/>

				<settings-site-recaptcha
					:recaptcha-site-key="recaptchaSiteKey"
					:recaptcha-secret-key="recaptchaSecretKey"
					:class="{ disabled: !connected }"
					@input="setValue"
				/>

				<div class="omapi-card-setting-section">
					<header></header>
					<div>
						<core-save-button
							size="large"
							color="green"
							:saving="isSaving"
							:disabled="!changed || !connected"
						/>
					</div>
				</div>
			</div>
		</common-card>
		<settings-pro-upsell v-if="showProUpsells" />

		<modals-pro-feature utm-medium="PoweredBySettings" :features="['remove-branding']" />
	</form>
</template>

<script lang="js">
import get from 'lodash/get';
import { mapState, mapActions, mapGetters } from 'vuex';
import { defaultState } from '@/settings/store/state';
import { backup } from '@/components/mixins/backup';
import { bulkWatchHandler } from '@/utils';

const pageSettings = [
	'globalInteractionCookie',
	'globalSuccessCookie',
	'globalAnalyticsID',
	'globalAnalyticsProfile',
	'defaultIntegration',
	'affiliateLink',
	'affiliateLinkPosition',
	'recaptchaSiteKey',
	'recaptchaSecretKey',
];
export default {
	mixins: [backup],
	data() {
		const defaults = defaultState()['siteSettings'];
		const data = {
			changed: false,
		};
		pageSettings.forEach((setting) => {
			data[setting] = defaults[setting];
		});

		return data;
	},
	computed: {
		...mapState(['error', 'alerts']),
		...mapGetters('settings', ['siteSettings']),
		...mapGetters(['isLiteUser', 'showProUpsells', 'connected']),
		isFetched() {
			return this.$store.getters.isFetched('siteSettings');
		},
		isLoading() {
			return this.$store.getters.isFetching('siteSettings') && !this.isFetched;
		},
		isSaving() {
			return this.$store.getters.isLoading('siteSettingsSave');
		},
		showTogglePoweredBy() {
			// TODO: Figure this out.
			//
			// This is likely tied to this functionality:
			// https://github.com/awesomemotive/optin-monster-app/blob/0e4070a539c761abbc01b7e5b66d74c9cbd0e3ef/plugins/omappv4-core/src/Database/Models/CampaignOption.php#L90-L96
			//
			// However, that doesn't turn off powered-by globally (but rather,
			// turns off off the default for future campaigns).
			return false;
		},
		pageSettings() {
			const settings = {};
			pageSettings.forEach((setting) => {
				settings[setting] = this[setting];
			});
			return settings;
		},
	},
	watch: {
		...bulkWatchHandler(pageSettings, 'checkChanged'),
		siteSettings(newValue) {
			pageSettings.forEach((setting) => {
				this[setting] = get(newValue, setting, this[setting]);
			});
			this.changed = false;
		},
	},
	beforeMount() {
		if (!this.isFetched) {
			this.fetch();
		}
	},
	mounted() {
		pageSettings.forEach((setting) => {
			this[setting] = this.$get(`siteSettings.${setting}`, this[setting]);
		});
	},
	methods: {
		...mapActions('settings', ['fetchSiteSettings', 'saveSiteSettings']),
		checkChanged() {
			this.changed = pageSettings.find((s) => this[s] !== this.$get(`siteSettings.${s}`));
		},
		saveChanges() {
			this.saveSiteSettings(this.pageSettings);
		},
		setSelectValue(value, event) {
			const name = get(event, 'name');
			if (name) {
				this[name] = value;
			}
		},
		setValue({ target }) {
			let { name } = target;
			let value = '';

			switch (target.type) {
				case 'checkbox':
					value = !!target.checked;
					break;
				case 'text':
				case 'url':
				case 'radio':
					value = target.value;
					break;
				case 'number':
					value = parseInt(target.value, 10);
					break;
			}

			this[name] = value;
		},
		fetch() {
			if (!this.isLoading) {
				this.fetchSiteSettings();
			}
		},
		maybeShowUpgradeModal(event) {
			if (this.showProUpsells) {
				event.stopPropagation();
				this.$modal.show('pro-feature');
			}
		},
		toggleAnalyticsSettings(show) {
			this.toggleBackup(show, ['globalAnalyticsID', 'globalAnalyticsProfile']);
		},
		toggleCookieSettings(key, show) {
			this.toggleBackup(show, {
				key,
				value: 0,
			});
		},
		toggleDefaultIntegration(show) {
			this.toggleBackup(show, 'defaultIntegration');
		},
	},
};
</script>
<style lang="scss" scoped>
.omapi-settings-page {
	position: relative;
	.omapi-settings-page__top-save {
		position: absolute;
		top: 12px;
		right: 0;
		margin-right: 0;
	}
}
</style>
